/*
 * The typed API client.
 *
 * TYPES ARE GENERATED, NEVER HAND-WRITTEN. `src/api/schema.d.ts` comes from
 * openapi.yaml via openapi-typescript (`npm run gen:api`), and openapi.yaml comes
 * from the Go handlers via huma. So a field renamed in Go breaks `tsc` here
 * rather than becoming an `undefined` that renders as a blank space at a venue.
 * That chain is the reason the generated document is committed: a contract change
 * shows up as a readable diff in review.
 *
 * NO FETCH LIBRARY. This is four functions over `fetch`. A query cache would earn
 * its weight on a screen with many independent resources; these screens each read
 * one endpoint, and Active's refresh policy is a deliberate design question (§9's
 * staleness calibration) rather than something to inherit from a library default.
 *
 * WHY THE HTTP STATUS IS PART OF THE TYPE. The session middleware distinguishes
 * 401 (nobody is signed in) from 403 (signed in, still pending approval) from 409
 * (approved, but onboarding never finished), and the client routes to three
 * different screens on that difference. Collapsing them into one Error would make
 * the pending screen unreachable.
 */

import type { components, paths } from "../api/schema";
import { setZoneSpans } from "./zones";

export type Schemas = components["schemas"];

/*
 * EVERY URL IN THIS FILE IS CHECKED AGAINST THE GENERATED CONTRACT, and that is a
 * bug fix rather than a nicety.
 *
 * Field NAMES were already checked — `Schemas[…]` comes from openapi.yaml, so a
 * renamed field breaks `tsc`. Paths were not, because a path was a template
 * string, and a template string is just text. The fencer search shipped as
 * `/api/fencers` against a server serving `/api/search/fencers`, and nothing
 * caught it: the 404 became an ApiError, the search's own `.catch()` cleared its
 * results, and the screen honestly reported "Nothing matching that yet." A
 * silently-empty search is the worst shape a wrong URL can take — it looks like an
 * answer.
 *
 * So `url()` takes a key of the generated `paths` map. `/api/fencers` is not one,
 * and writing it is now a type error at the call site. Path parameters are
 * substituted from the same template the document declares, which means a renamed
 * parameter is caught too.
 */
type KnownPath = keyof paths;

function url(
  template: KnownPath,
  params?: Record<string, string>,
  query?: Record<string, string | undefined>,
): string {
  let out: string = template;

  for (const [key, value] of Object.entries(params ?? {})) {
    // encodeURIComponent on the VALUE only: a uuid needs no escaping today, but
    // these are ids from a payload rather than constants, and a path segment that
    // ever carries a slash would otherwise change which endpoint is called.
    out = out.replace(`{${key}}`, encodeURIComponent(value));
  }

  // An absent or empty query value is omitted entirely rather than sent as `key=`,
  // so the unfiltered form of a request has one canonical URL.
  const pairs = Object.entries(query ?? {}).filter(
    (entry): entry is [string, string] => !!entry[1],
  );
  if (pairs.length === 0) return out;

  const search = new URLSearchParams(pairs);
  return `${out}?${search.toString()}`;
}

export type ActivePayload = Schemas["ActiveOutputBody"];
export type ActiveTournament = Schemas["ActiveTournamentBody"];
export type ActiveEvent = Schemas["ActiveEventBody"];
export type ActiveFencer = Schemas["ActiveFencerBody"];
/** One person on a team's roster, on Active, Upcoming and Past (decision 714). */
export type TeamMember = Schemas["TeamMemberBody"];
export type User = Schemas["UserBody"];

export type Alert = Schemas["AlertBody"];
export type AlertsPayload = Schemas["GetAlertsOutputBody"];
export type AlertPreferences = Schemas["AlertPreferencesOutputBody"];
/**
 * The same ladder, merged across the lists in the caller's notifications scope
 * (multi-list build item 4, decision 516).
 *
 * A SEPARATE TYPE RATHER THAN A WIDENED `AlertPreferences`, because the bodies
 * differ in exactly one field and it is the one a caller must not confuse: this
 * one names the SET it merged (`list_ids`) where the per-list body names a
 * single `list_id`. `PreferenceTree` takes either, since it reads neither.
 */
export type ViewAlertPreferences = Schemas["ViewAlertPreferencesOutputBody"];
export type AlertPreferenceCell = Schemas["AlertPreferenceCell"];
export type AlertPreferenceGroup = Schemas["AlertPreferenceGroupBody"];
export type AlertPreferenceKind = Schemas["AlertPreferenceKindBody"];
/**
 * The four rungs of decision 486's ladder, narrowed by the contract. A scope
 * the server does not know is a type error here rather than a 422 at the venue
 * -- the same argument the channel alias below makes.
 */
export type AlertPreferenceScope = Schemas["AlertPreferenceInput"]["scope"];
/**
 * The channel vocabulary, narrowed by the generated contract rather than left as
 * `string`. A typo'd channel is a type error here instead of a 422 at the venue.
 */
export type AlertChannel = Schemas["AlertPreferenceInput"]["channel"];

/**
 * What `/api/me` answers: who is here, and whether anything is live for them.
 *
 * Two facts rather than one because they have different lifetimes — the user
 * changes on sign-in and preference edits, the count changes when a tournament
 * starts — and collapsing them onto the user object would imply the count is an
 * account attribute that every endpoint returning a user has to compute.
 */
export interface Me {
  user: User | null;
  activeEventCount: number;
  /**
   * The Active tab's own two facts (decision 546), which are NOT
   * `activeEventCount`: that count stays true for a day after everyone has gone
   * home, because its only time bound is the tournament's date window.
   *
   * `activeFencerCount` is the tracked population whose card is on screen for a
   * reason that does not age out; `lastFencerDoneAt` is when the rest of them
   * finished. `activeTabVisible` in lib/grace.ts turns the pair into the answer,
   * with the same window that retires a fencer card.
   */
  activeFencerCount: number;
  /** Absent when nobody tracked has finished inside the live window. */
  lastFencerDoneAt?: string;
  /**
   * Unread alerts for this account (decision 400: newer than the seen watermark
   * and not superseded).
   *
   * On /api/me for the same reason `activeEventCount` is: the shell renders on
   * every screen and this is the one call made before first paint. NOT scoped to
   * the current list — read state is per user, because there is one feed.
   */
  unreadAlertCount: number;
  /** The build this server was compiled from (decision 361): "dev"/"unknown"
   *  outside a release image. The Settings footer shows them so a defect report
   *  can name the build. */
  serverVersion: string;
  serverCommit: string;

  /**
   * The feature gates this account holds (decision 369).
   *
   * THE SERVER HAS ALWAYS SENT THIS AND THE CLIENT HAS NEVER READ IT. Multi-list
   * is the first capability with chrome to hide, so this is where the array
   * starts being used — by `useMultiList`, and only there.
   *
   * IT IS NOT THE ENFORCEMENT and must never be treated as it. Every gated
   * endpoint is refused in `sessionMiddleware` whatever the client believes;
   * this exists so the UI can agree with the server rather than offer a control
   * that 403s. An admin bypasses every gate server-side and therefore appears
   * here holding everything, which is why the dev admin account sees gated
   * chrome that an ordinary account does not.
   */
  features: string[];

  /**
   * The VAPID application server key this deployment pushes with, base64url.
   *
   * On /api/me for the reason everything else here is: the Settings control needs
   * it at the moment a user GESTURE arrives — `pushManager.subscribe` must be
   * called from inside the gesture handler or the browser refuses the permission
   * prompt — and fetching it then would put a network round trip between the tap
   * and the prompt.
   *
   * EMPTY MEANS THIS BUILD CANNOT ENROL A DEVICE, which the control treats as the
   * same state a browser with no push support gives it. It should never be empty
   * against a real server: cmd/api refuses to start without a keypair.
   */
  pushPublicKey: string;

  /**
   * The two list caps (decision 889): runtime configuration on the server, so
   * they are read from it rather than written here. The member cap is the one
   * onboarding renders before the account has a list to carry it; every list
   * and roster response carries it too. Absent when the server could not read
   * its configuration, which leaves the count unstated rather than guessed.
   */
  listMemberCap?: number;
  listCountCap?: number;
}

/** What went wrong, in the vocabulary the router cares about. */
export type ApiFailure =
  | { kind: "unauthenticated" } // 401 — sign-in
  | { kind: "pending" } // 403 — the pending screen
  | { kind: "onboarding-incomplete" } // 409 — the fencer search
  | { kind: "offline" } // the request never landed
  | { kind: "server"; status: number; detail: string };

export class ApiError extends Error {
  readonly failure: ApiFailure;

  constructor(failure: ApiFailure, message: string) {
    super(message);
    this.name = "ApiError";
    this.failure = failure;
  }
}

/**
 * Whether a failure was a 409.
 *
 * READ THE VARIANT NAME WITH CARE: every 409 in this app arrives as
 * `onboarding-incomplete`, because that is what a 409 means on the view
 * endpoints the router was written around — an approved account with no list.
 * It is NOT what a 409 means on a verb, where it is a refusal: the roster cap,
 * a list name already taken, the list cap, a primary that may not be deleted.
 *
 * SO A SCREEN READS THIS IN ITS OWN LOCAL MEANING, which is the posture
 * AccountManagement already takes with its delete refusal. This helper exists so
 * the reading is deliberate rather than a `kind` comparison that looks like a
 * mistake — and because the obvious test, `kind === "server" && status === 409`,
 * is one this vocabulary can never satisfy. Manage fencers had exactly that
 * test guarding its at-cap sentence, so the sentence was unreachable and the
 * eleventh fencer reported a generic save failure.
 */
export function isConflict(err: unknown): boolean {
  return err instanceof ApiError && err.failure.kind === "onboarding-incomplete";
}

/**
 * huma's error envelope (RFC 7807-shaped). Read for `detail` only: the rest is
 * for a developer reading a response body, and none of it is shown to a user —
 * §4's voice rules mean user-facing copy is ours and localized, never a
 * server string passed through.
 */
interface HumaError {
  detail?: string;
  errors?: { message?: string }[];
}

function failureFor(status: number, detail: string): ApiFailure {
  switch (status) {
    case 401:
      return { kind: "unauthenticated" };
    case 403:
      return { kind: "pending" };
    case 409:
      return { kind: "onboarding-incomplete" };
    default:
      return { kind: "server", status, detail };
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      // The session is an HttpOnly cookie on the same origin (decision 7), so
      // this is same-origin rather than "include" — there is no cross-site
      // request to make, and saying so keeps it that way.
      credentials: "same-origin",
    });
  } catch (cause) {
    // A venue's wifi is the normal case for this, not an exception. §9's
    // staleness treatment is what covers it visually; this is just the fact.
    throw new ApiError({ kind: "offline" }, `${path}: ${String(cause)}`);
  }

  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = (await resp.json()) as HumaError;
      if (body.detail) detail = body.detail;
    } catch {
      // A non-JSON error body is not itself an error worth reporting over the
      // status code that produced it.
    }
    throw new ApiError(failureFor(resp.status, detail), `${path}: ${resp.status} ${detail}`);
  }

  // A 204 has no body by construction (the endpoints that revoke a session, sign
  // out, or delete an account answer this way). Parsing it as JSON would throw on
  // success, turning a completed action into a reported failure — so return
  // nothing without reading a body that is not there.
  //
  // 202 likewise: the mail endpoints answer "accepted" with no body (decisions
  // 604-608), and password reset in particular must say nothing more, since its
  // whole contract is that every request gets the same answer.
  if (resp.status === 204 || resp.status === 202) {
    return undefined as T;
  }

  return (await resp.json()) as T;
}

export type SessionRow = Schemas["SessionBody"];
export type LocaleRow = Schemas["LocaleBody"];
export type EmailSummary = Omit<Schemas["EmailSummaryBody"], "$schema">;
export type FencerResult = Schemas["FencerSearchBody"];
export type ClubResult = Schemas["ClubSearchBody"];
export type RosterMember = Schemas["RosterMemberBody"];
export type Provider = Schemas["ProviderBody"];
export type PendingUser = Schemas["PendingUserBody"];
export type AliasQueueRow = Schemas["AliasQueueBody"];
export type AliasCandidate = Schemas["AliasCandidateBody"];
export type AliasEvidence = Schemas["AliasEvidenceBody"];
export type AliasGroup = Schemas["AliasGroupBody"];
export type EventsFenced = Schemas["EventsFencedBody"];
export type AdminAccount = Schemas["AdminUserBody"];
export type DuplicateFencerCandidate = Schemas["DuplicateFencerCandidateBody"];
export type DuplicateComparison = Schemas["DuplicateComparisonBody"];
export type FencerFacts = Schemas["FencerFactsBody"];

/**
 * The per-view list scope (decisions 501, 502).
 *
 * `ViewName` is DERIVED from the response rather than written out, so a fifth
 * view added to the contract is a type error at every call site instead of a
 * string the server does not know. `$schema` is huma's own key and is not a view.
 */
export type ViewLists = Schemas["ViewListsOutputBody"];
export type ViewName = Exclude<keyof ViewLists, "$schema">;
export type ViewListScope = Schemas["ViewScopeBody"]["scope"];
export type AdminDevice = Schemas["AdminDeviceBody"];
export type AdminPushAccount = Schemas["AdminPushAccountBody"];

/** How long `api.me()` waits before treating the server as unreachable (decision 887). */
export const ME_TIMEOUT_MS = 30_000;

export const api = {
  /**
   * The signed-in user, or null — plus what the shell needs to draw itself.
   *
   * PUBLIC, and 200 with `user: null` when nobody is signed in — deliberately not
   * a 401. It is the one call the app makes before it knows whether it has a
   * session, so "nobody" has to be an answer rather than a failure.
   *
   * `activeEventCount` rides along rather than being a second request, because
   * the shell renders on every screen and a cold load straight to /past still has
   * to know whether the Active tab exists (decision 324).
   */
  async me(): Promise<Me> {
    // A TIME LIMIT OF ITS OWN (decision 887). fetch has none, so a request that
    // stalled before reaching the server left a cold load on a blank page until
    // Cloud Run's 300 s cutoff. 30 s rather than less because a venue's signal
    // can be genuinely slow (Kevin, 2026-10-25); past it the call fails as
    // "offline" and main.tsx shows the reconnecting screen, which keeps trying.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ME_TIMEOUT_MS);
    let body: Schemas["MeOutputBody"];
    try {
      body = await request<Schemas["MeOutputBody"]>(url("/api/me"), { signal: controller.signal });
    } finally {
      clearTimeout(timer);
    }
    // Every venue zone's offsets (decision 785), set before anything renders a
    // time: this is the call made before first paint and on every wake.
    setZoneSpans(body.zones);
    return {
      // huma types a nullable body field as non-optional; the server really does
      // send null here, which is what the cast acknowledges.
      user: (body.user as User | null) ?? null,
      activeEventCount: body.active_event_count,
      activeFencerCount: body.active_fencer_count,
      lastFencerDoneAt: body.last_fencer_done_at,
      unreadAlertCount: body.unread_alert_count,
      serverVersion: body.server_version,
      serverCommit: body.server_commit,
      pushPublicKey: body.push_public_key,
      features: list(body.features),
      ...(body.list_member_cap !== undefined ? { listMemberCap: body.list_member_cap } : {}),
      ...(body.list_count_cap !== undefined ? { listCountCap: body.list_count_cap } : {}),
    };
  },

  active(): Promise<ActivePayload> {
    return request<ActivePayload>(url("/api/active"));
  },

  /**
   * Upcoming, with the same server-side search Past has, over the same three
   * scopes — tournament, fencer and event names.
   *
   * Server-side for a different reason than Past's, which is worth knowing: Past
   * grows forever, while Upcoming is bounded by the calendar. What makes it a
   * server concern anyway is that ONE query has to serve both the filtered and
   * unfiltered list, so the two cannot come to disagree about what is in scope.
   *
   * An empty query sends no parameter at all rather than `search=`, so the
   * unfiltered request keeps one canonical URL.
   */
  upcoming(search = ""): Promise<Schemas["UpcomingOutputBody"]> {
    return request(url("/api/upcoming", {}, { search: search.trim() }));
  },

  /**
   * Past, with SERVER-SIDE search (§11: "this view grows forever; client-side
   * filtering over a full download doesn't scale").
   *
   * An empty query is sent as no parameter at all rather than `search=`, so the
   * unfiltered request has one canonical form — which matters for anything that
   * ever caches by URL.
   */
  past(search = ""): Promise<Schemas["PastOutputBody"]> {
    return request(url("/api/past", {}, { search: search.trim() }));
  },

  // --- notifications ---------------------------------------------------------

  /**
   * One page of the notifications feed, newest first.
   *
   * THE CURSOR IS OPAQUE TO THIS LAYER: both halves come straight out of the
   * previous response and go straight back, because the server's ordering key is
   * the server's business. Sending one without the other is a 422 rather than a
   * silent reset to the top — see the endpoint's own note.
   */
  alerts(cursor?: { observedAt: string; id: string }): Promise<AlertsPayload> {
    return request(
      url("/api/alerts", {}, {
        before_observed_at: cursor?.observedAt,
        before_id: cursor?.id,
      }),
    );
  },

  /**
   * Advance the seen watermark. Opening the feed does this, and so does "mark all
   * as read" — one verb, because they are the same act.
   *
   * `observedAt` IS THE FEED'S OWN `newest_observed_at`, not the client's clock.
   * Using `Date.now()` here would mark read anything written between the fetch
   * and this call — an alert the badge announced and the reader never saw.
   */
  markAlertsSeen(observedAt: string): Promise<unknown> {
    return request(url("/api/alerts/seen"), {
      method: "POST",
      body: JSON.stringify({ seen_at: observedAt }),
    });
  },

  /**
   * "Delete all", for this account only. Nothing leaves the ledger: the record of
   * what the app said is durable (§4.1) and this is per-user visibility.
   */
  clearAlerts(observedAt: string): Promise<unknown> {
    return request(url("/api/alerts/clear"), {
      method: "POST",
      body: JSON.stringify({ cleared_at: observedAt }),
    });
  },

  /** Dismiss one alert. Idempotent server-side, which is what makes undo safe. */
  hideAlert(id: string): Promise<unknown> {
    return request(url("/api/alerts/{id}", { id }), { method: "DELETE" });
  },

  /** Undo a dismissal — the roster screen's remove-then-undo, for one alert. */
  restoreAlert(id: string): Promise<unknown> {
    return request(url("/api/alerts/{id}/restore", { id }), { method: "POST" });
  },

  /**
   * The alert preference matrix for one list, EFFECTIVE: defaults filled in, with
   * `is_default` saying which cells are the account's own choice (decision 394).
   */
  alertPreferences(listID: string): Promise<AlertPreferences> {
    return request(url("/api/lists/{id}/alert-preferences", { id: listID }));
  },

  /**
   * Save the cells that changed.
   *
   * PARTIAL, unlike `preferences()` above, which is whole-object and resets what
   * it omits. Decision 394 split them for that reason: a whole-object matrix
   * would make every caller carry every toggle.
   */
  updateAlertPreferences(
    listID: string,
    preferences: Schemas["AlertPreferenceInput"][],
  ): Promise<AlertPreferences> {
    return request(url("/api/lists/{id}/alert-preferences", { id: listID }), {
      method: "PUT",
      body: JSON.stringify({ preferences }),
    });
  },

  /**
   * The ladder for the lists in the caller's NOTIFICATIONS SCOPE, merged
   * server-side (decision 516).
   *
   * The scope travels nowhere: it is stored per user per view (502) and resolved
   * on every read, so this takes no arguments and cannot be asked about a list
   * the caller does not hold. A cell reads `on` only where every list in scope
   * resolves on, `off` only where every one resolves off, and `mixed` otherwise
   * -- the same word decision 486 already uses for kinds within one list that
   * disagree.
   */
  viewAlertPreferences(): Promise<ViewAlertPreferences> {
    return request(url("/api/me/alert-preferences"));
  },

  /**
   * Save the cells that changed, to EVERY list in the notifications scope, in
   * one transaction (decision 517).
   *
   * ONE REQUEST WHATEVER THE SCOPE HOLDS, which is the difference from the plan
   * the item was written against: it had the page PUT once per list and merge
   * the responses, and a partial failure there would leave a cell in a state the
   * reader did not ask for. The answer is the re-merged tree.
   */
  setViewAlertPreferences(
    preferences: Schemas["AlertPreferenceInput"][],
  ): Promise<ViewAlertPreferences> {
    return request(url("/api/me/alert-preferences"), {
      method: "PUT",
      body: JSON.stringify({ preferences }),
    });
  },

  // --- push ------------------------------------------------------------------

  /**
   * Enrol this device, or move an endpoint another account holds onto this one.
   *
   * IDEMPOTENT AND RE-SENT ON EVERY VISIT to the notifications page, which is
   * what makes the two sides self-healing without a read endpoint: if the browser
   * holds a subscription and the server's row was pruned — a transient 410, an
   * account deleted and re-made — this puts it back. Nothing ever reads a
   * subscription back out; the rows are credentials and the browser already has
   * its own copy.
   */
  subscribePush(sub: PushSubscriptionJSON): Promise<void> {
    return request(url("/api/push/subscription"), {
      method: "PUT",
      body: JSON.stringify({
        endpoint: sub.endpoint,
        p256dh: sub.keys?.p256dh ?? "",
        auth: sub.keys?.auth ?? "",
      }),
    });
  },

  /**
   * Stop sending to one device.
   *
   * CALLED BY SIGN-OUT AS WELL AS BY THE CONTROL, and that is not tidiness: a
   * browser's push subscription belongs to the browser profile and outlives a
   * sign-out, so a row left behind would send one account's fencer's strip
   * assignments to whoever signs in on that phone next.
   */
  unsubscribePush(endpoint: string): Promise<void> {
    return request(url("/api/push/unsubscribe"), {
      method: "POST",
      body: JSON.stringify({ endpoint }),
    });
  },

  /**
   * Which languages this build can render in (decision 537).
   *
   * Public, like `providers()` and for the same reason: the sign-in screen is
   * a screen too, and a reader who cannot read English has to be able to
   * change the language before they can sign in.
   */
  locales(): Promise<Schemas["LocalesOutputBody"]> {
    return request(url("/api/locales"));
  },

  // --- auth ------------------------------------------------------------------

  /** Which sign-in methods actually exist. See providers() in the sign-in screen. */
  providers(): Promise<Schemas["AuthProvidersOutputBody"]> {
    return request(url("/api/auth/providers"));
  },

  signIn(email: string, password: string): Promise<Schemas["SessionResponseBody"]> {
    return request(url("/api/auth/sign-in"), {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },

  register(
    email: string,
    password: string,
    displayName: string,
  ): Promise<Schemas["SessionResponseBody"]> {
    return request(url("/api/auth/register"), {
      method: "POST",
      body: JSON.stringify({ email, password, display_name: displayName }),
    });
  },

  signOut(): Promise<unknown> {
    return request(url("/api/auth/sign-out"), { method: "POST" });
  },

  sessions(): Promise<Schemas["ListSessionsOutputBody"]> {
    return request(url("/api/sessions"));
  },

  revokeSession(id: string): Promise<unknown> {
    return request(url("/api/sessions/{id}", { id }), { method: "DELETE" });
  },

  preferences(body: Schemas["UpdatePreferencesInputBody"]): Promise<Schemas["UserOutputBody"]> {
    return request(url("/api/me/preferences"), { method: "PUT", body: JSON.stringify(body) });
  },

  changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<Schemas["ChangePasswordOutputBody"]> {
    return request(url("/api/me/password"), {
      method: "PUT",
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },

  /**
   * Delete this account (decision 891). The confirmation is the password when
   * the account has one, else `confirm: "delete"` once the reader has typed the
   * word; the server checks whichever applies and answers 400 for a wrong
   * password, 422 for anything but the word and 409 for the last approved admin.
   */
  deleteMyAccount(
    confirmation: { current_password: string } | { confirm: "delete" },
  ): Promise<unknown> {
    return request(url("/api/me"), { method: "DELETE", body: JSON.stringify(confirmation) });
  },

  // --- the address: verification, reset, change (stage 4b phase D) ----------
  //
  // Every link the server mails lands on an SPA page that POSTs its token from a
  // button press. A GET of the link changes nothing, because mail scanners
  // prefetch links; the state change is always one of these calls.

  /** Mail another verification link. 429 inside the cooldown, 502 on a failed send. */
  requestEmailVerification(): Promise<void> {
    return request(url("/api/me/email/verification"), { method: "POST" });
  },

  verifyEmail(token: string): Promise<Schemas["EmailBody"]> {
    return request(url("/api/auth/email/verify"), {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  },

  /** Always resolves the same way, whether or not the address has an account. */
  requestPasswordReset(email: string): Promise<void> {
    return request(url("/api/auth/password-reset"), {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  /** Sets the password and signs this device in; every other session ends. */
  confirmPasswordReset(token: string, newPassword: string): Promise<Schemas["SessionResponseBody"]> {
    return request(url("/api/auth/password-reset/confirm"), {
      method: "POST",
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  },

  /** Mails a confirmation link to the NEW address; nothing changes until it is opened. */
  requestEmailChange(newEmail: string, currentPassword?: string): Promise<void> {
    return request(url("/api/me/email"), {
      method: "POST",
      body: JSON.stringify({
        new_email: newEmail,
        ...(currentPassword ? { current_password: currentPassword } : {}),
      }),
    });
  },

  confirmEmailChange(token: string): Promise<Schemas["EmailBody"]> {
    return request(url("/api/auth/email/change/confirm"), {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  },

  /** The link's own Cancel: spends it, and the address stays as it was. */
  cancelEmailChangeByToken(token: string): Promise<void> {
    return request(url("/api/auth/email/change/cancel"), {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  },

  /** Withdraw the change in flight; answers the user without `pending_email`. */
  cancelEmailChange(): Promise<Schemas["UserOutputBody"]> {
    return request(url("/api/me/email/pending"), { method: "DELETE" });
  },

  /** A fresh link to the address already in flight. 429 inside the cooldown. */
  resendEmailChange(): Promise<void> {
    return request(url("/api/me/email/pending/resend"), { method: "POST" });
  },

  // --- email summaries (stage 4b phase E) ------------------------------------
  //
  // A separate control from Manage notifications (decision 612): email is not
  // an alert channel, and these five settings are the account's, not a list's.

  emailSummary(): Promise<EmailSummary> {
    return request(url("/api/me/email-summary"));
  },

  /** The whole object. 400 when summaries are on with nothing chosen. */
  setEmailSummary(body: EmailSummary): Promise<EmailSummary> {
    return request(url("/api/me/email-summary"), { method: "PUT", body: JSON.stringify(body) });
  },

  /**
   * The unsubscribe link's confirmation (decision 621). No session needed:
   * the token is the credential. The POST is the change; the link's GET only
   * brought the reader here.
   */
  unsubscribeEmailSummary(token: string): Promise<void> {
    return request(url("/api/email/unsubscribe", {}, { token }), { method: "POST" });
  },

  // --- passkeys --------------------------------------------------------------
  //
  // Two round trips each, because that is what WebAuthn is: the server issues a
  // challenge, the authenticator signs it, the server verifies. The ceremony id
  // is single-use and server-held (decision 309) rather than a signed blob
  // travelling through the client.

  beginPasskeyLogin(): Promise<Schemas["CeremonyBegunBody"]> {
    return request(url("/api/auth/passkeys/login/begin"), { method: "POST" });
  },

  finishPasskeyLogin(
    ceremonyID: string,
    credential: unknown,
  ): Promise<Schemas["SessionResponseBody"]> {
    return request(url("/api/auth/passkeys/login/finish"), {
      method: "POST",
      body: JSON.stringify({ ceremony_id: ceremonyID, credential }),
    });
  },

  beginPasskeyRegistration(): Promise<Schemas["CeremonyBegunBody"]> {
    return request(url("/api/auth/passkeys/register/begin"), { method: "POST" });
  },

  finishPasskeyRegistration(
    ceremonyID: string,
    credential: unknown,
    name?: string,
  ): Promise<unknown> {
    return request(url("/api/auth/passkeys/register/finish"), {
      method: "POST",
      body: JSON.stringify({ ceremony_id: ceremonyID, credential, name }),
    });
  },

  passkeys(): Promise<Schemas["PasskeyListOutputBody"]> {
    return request(url("/api/auth/passkeys"));
  },

  deletePasskey(id: string): Promise<unknown> {
    return request(url("/api/auth/passkeys/{id}", { id }), { method: "DELETE" });
  },

  // --- onboarding and roster -------------------------------------------------

  completeOnboarding(body: Schemas["OnboardingInputBody"]): Promise<{
    user: User;
    list: Schemas["ListBody"];
  }> {
    return request(url("/api/onboarding"), { method: "POST", body: JSON.stringify(body) });
  },

  searchFencers(q: string): Promise<Schemas["SearchFencersOutputBody"]> {
    return request(url("/api/search/fencers", {}, { q }));
  },

  searchClubs(q: string): Promise<Schemas["SearchClubsOutputBody"]> {
    return request(url("/api/search/clubs", {}, { q }));
  },

  lists(): Promise<Schemas["GetListsOutputBody"]> {
    return request(url("/api/lists"));
  },

  /*
   * THE LIST VERBS (multi-list build item 2). Every one of these is refused
   * with 403 for an account without `multi_list`, in the session middleware
   * rather than in a handler — so a caller that renders them without checking
   * `useMultiList().granted` is offering a control that cannot work, which is
   * the thing decision 418 rules out.
   */

  /** Create a list. 409 for a name already taken, and 409 at the list cap. */
  createList(body: Schemas["CreateListInputBody"]): Promise<Schemas["ListBody"]> {
    return request(url("/api/lists"), { method: "POST", body: JSON.stringify(body) });
  },

  /**
   * Rename or re-describe. PARTIAL: a field left out is left alone, so a rename
   * does not blank a description.
   */
  updateList(
    listID: string,
    body: Schemas["UpdateListInputBody"],
  ): Promise<Schemas["ListBody"]> {
    return request(url("/api/lists/{id}", { id: listID }), {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },

  /**
   * Delete a list. 409 for the primary, which is what makes the last list
   * undeletable.
   *
   * 204, so nothing is parsed — and the caller re-reads BOTH `/api/me` and the
   * lists afterwards, because two facts changed: which lists exist, and
   * possibly which one is current. The server re-points `current_list_id` at
   * the primary when it deletes the current list, and the client only learns
   * that by asking.
   */
  deleteList(listID: string): Promise<void> {
    return request(url("/api/lists/{id}", { id: listID }), { method: "DELETE" });
  },

  /** Move the primary designation. Answers the caller's lists, because two rows changed. */
  setPrimaryList(listID: string): Promise<Schemas["GetListsOutputBody"]> {
    return request(url("/api/me/primary-list"), {
      method: "PUT",
      body: JSON.stringify({ list_id: listID }),
    });
  },

  /**
   * Which list Manage fencers is editing (decision 501's narrowing of
   * `current_list_id`).
   *
   * UNGATED, unlike the verbs above: every account has a current list, and this
   * is how the roster screen remembers which one is open. Called right after a
   * create, so the list somebody just made is the one they are then putting
   * fencers into.
   */
  currentList(listID: string): Promise<Schemas["UserOutputBody"]> {
    return request(url("/api/me/current-list"), {
      method: "PUT",
      body: JSON.stringify({ list_id: listID }),
    });
  },

  /**
   * Copy the primary list's notification overrides onto another list.
   *
   * The "Same as <primary>" action on the post-create step. An endpoint rather
   * than a client-side replay of the ladder: replaying resolved values would
   * write a row per kind and freeze today's defaults into the new list, where
   * copying the stored overrides keeps the two lists agreeing about kinds that
   * do not exist yet.
   */
  copyAlertPreferencesFromPrimary(listID: string): Promise<AlertPreferences> {
    return request(
      url("/api/lists/{id}/alert-preferences/copy-from-primary", { id: listID }),
      { method: "POST" },
    );
  },

  /**
   * Which lists scope each view (decision 501), with what each one resolves to
   * right now.
   *
   * THE RESOLVED IDS ARE NOT DERIVABLE HERE, which is why they travel: `all` and
   * `primary` are computed server-side on every read, so a client holding the
   * mode alone would not know which lists it is looking at — and after a list is
   * created or the primary flag moves, its own answer would be stale.
   *
   * Nothing calls this yet: the pickers arrive with build items 3 and 4. It ships
   * with the storage so the contract lands in one review rather than two.
   */
  viewLists(): Promise<ViewLists> {
    return request(url("/api/me/view-lists"));
  },

  /**
   * Set one view's scope. Whole-object per view, like `preferences` above.
   *
   * `list_ids` belongs to the `selected` scope alone: required and non-empty
   * there, refused with 422 under the other two, which resolve without a stored
   * set. Answers the whole four-view object, so a caller replaces its state
   * rather than merging its own write into it.
   */
  setViewLists(view: ViewName, body: Schemas["SetViewListsInputBody"]): Promise<ViewLists> {
    return request(url("/api/me/view-lists/{view}", { view }), {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  roster(listID: string): Promise<Schemas["GetRosterOutputBody"]> {
    return request(url("/api/lists/{id}/members", { id: listID }));
  },

  /**
   * A club's fencers.
   *
   * Paginated, and there is deliberately no bulk variant (contract §3): a
   * 10-fencer cap against a 75-member club makes select-all a trap, so the API
   * never offers the shape that would invite it.
   */
  clubMembers(clubID: string, offset = 0): Promise<Schemas["ClubMembersOutputBody"]> {
    return request(
      url("/api/clubs/{id}/members", { id: clubID }, { offset: String(offset) }),
    );
  },

  /**
   * Track one fencer. One call per fencer, deliberately: the cap is enforced
   * server-side per insert, so adding three when two fit adds two and refuses the
   * third with a 409 that names which one failed. A bulk endpoint would have to
   * choose between all-or-nothing and a partial success the client cannot
   * describe.
   */
  addRosterMember(listID: string, fencerID: string): Promise<Schemas["GetRosterOutputBody"]> {
    return request(url("/api/lists/{id}/members", { id: listID }), {
      method: "POST",
      body: JSON.stringify({ fencer_id: fencerID }),
    });
  },

  /** Stop tracking. Idempotent server-side, which is what makes undo safe to press twice. */
  removeRosterMember(
    listID: string,
    fencerID: string,
  ): Promise<Schemas["GetRosterOutputBody"]> {
    return request(
      url("/api/lists/{id}/members/{fencer_id}", { id: listID, fencer_id: fencerID }),
      { method: "DELETE" },
    );
  },

  // --- admin -----------------------------------------------------------------
  //
  // Every verb returns the refreshed queue rather than an acknowledgement, so the
  // screen's list and its count are always one server response rather than a
  // local edit plus a re-fetch that could disagree.

  pendingUsers(): Promise<Schemas["AdminPendingUsersOutputBody"]> {
    return request(url("/api/admin/pending-users"));
  },

  approveUser(id: string): Promise<Schemas["AdminPendingUsersOutputBody"]> {
    return request(url("/api/admin/pending-users/{id}/approve", { id }), { method: "POST" });
  },

  aliasQueue(): Promise<Schemas["AdminAliasQueueOutputBody"]> {
    return request(url("/api/admin/aliases"));
  },

  /** The queue's size alone, for the hub's link (decision 778). */
  aliasQueueCount(): Promise<Schemas["AdminCountOutputBody"]> {
    return request(url("/api/admin/aliases/count"));
  },

  confirmAlias(
    aliasID: string,
    fencerID: string,
  ): Promise<Schemas["AdminAliasQueueOutputBody"]> {
    return request(url("/api/admin/aliases/{id}/confirm", { id: aliasID }), {
      method: "POST",
      body: JSON.stringify({ fencer_id: fencerID }),
    });
  },

  createFencerForAlias(aliasID: string): Promise<Schemas["AdminAliasQueueOutputBody"]> {
    return request(url("/api/admin/aliases/{id}/create-fencer", { id: aliasID }), {
      method: "POST",
    });
  },

  rejectAlias(aliasID: string): Promise<Schemas["AdminAliasQueueOutputBody"]> {
    return request(url("/api/admin/aliases/{id}/reject", { id: aliasID }), { method: "POST" });
  },

  /**
   * Rule that ONE candidate is not this alias's fencer (decision 474).
   *
   * Not a fourth alias verb: the three above DECIDE an alias and clear it from
   * the queue, where this narrows the list the reviewer is deciding from and
   * leaves the alias pending. It returns the refreshed queue like the others, so
   * the shortened candidate list comes from server truth rather than a local
   * filter that would evaporate on reload.
   */
  rejectAliasCandidate(
    aliasID: string,
    fencerID: string,
  ): Promise<Schemas["AdminAliasQueueOutputBody"]> {
    return request(
      url("/api/admin/aliases/{id}/candidates/{fencerID}/reject", {
        id: aliasID,
        fencerID,
      }),
      { method: "POST" },
    );
  },

  /**
   * Several candidates are one person, and this alias is them (decision 981):
   * each merged fencer folds into the survivor, then the alias is confirmed to
   * it, in one transaction. Decides the alias, like confirm.
   */
  mergeAndConfirmAlias(
    aliasID: string,
    survivorFencerID: string,
    mergedFencerIDs: string[],
  ): Promise<Schemas["AdminAliasQueueOutputBody"]> {
    return request(url("/api/admin/aliases/{id}/merge-and-confirm", { id: aliasID }), {
      method: "POST",
      body: JSON.stringify({ survivor_fencer_id: survivorFencerID, merged_fencer_ids: mergedFencerIDs }),
    });
  },

  /** `rejectAliasCandidate` for several candidates at once, in one transaction. */
  rejectAliasCandidates(
    aliasID: string,
    fencerIDs: string[],
  ): Promise<Schemas["AdminAliasQueueOutputBody"]> {
    return request(url("/api/admin/aliases/{id}/reject-candidates", { id: aliasID }), {
      method: "POST",
      body: JSON.stringify({ fencer_ids: fencerIDs }),
    });
  },

  // THE DUPLICATE-FENCER QUEUE (migration 0036, decision 525).

  duplicateFencers(): Promise<Schemas["AdminDuplicateFencersOutputBody"]> {
    return request(url("/api/admin/duplicate-fencers"));
  },

  /** The number of pairs alone, for the hub's link (decision 778). */
  duplicateFencersCount(): Promise<Schemas["AdminCountOutputBody"]> {
    return request(url("/api/admin/duplicate-fencers/count"));
  },

  /**
   * These are the same person. Merges through the same identity.MergeFencers
   * path `ingest identity-merge` uses (Kevin's ruling, 2026-09-16); the pair
   * drops out of the refreshed queue this returns because the duplicate side
   * becomes merged_into-set.
   */
  mergeDuplicateFencer(
    survivorID: string,
    duplicateID: string,
  ): Promise<Schemas["AdminDuplicateFencersOutputBody"]> {
    return request(
      url("/api/admin/duplicate-fencers/{survivorID}/{duplicateID}/merge", {
        survivorID,
        duplicateID,
      }),
      { method: "POST" },
    );
  },

  rejectDuplicateFencer(
    survivorID: string,
    duplicateID: string,
  ): Promise<Schemas["AdminDuplicateFencersOutputBody"]> {
    return request(
      url("/api/admin/duplicate-fencers/{survivorID}/{duplicateID}/reject", {
        survivorID,
        duplicateID,
      }),
      { method: "POST" },
    );
  },

  // Account management (roadmap item 26). Search, then a detail read for the
  // account's lists, then a delete that echoes the email as a typed confirmation.
  /**
   * List accounts, optionally narrowed by a substring, one page at a time.
   *
   * AN EMPTY `q` LISTS EVERYTHING (decision 474). The screen used to require a
   * search before it would show anything; searching is how an admin narrows the
   * list, not how they reach it. One endpoint serves both, so the listed and the
   * filtered view cannot disagree about scope — decision 329's rule for the
   * temporal views' search.
   */
  searchAccounts(
    q: string,
    page: { limit?: number; offset?: number } = {},
  ): Promise<Schemas["AdminSearchUsersOutputBody"]> {
    const params = new URLSearchParams({ q });
    if (page.limit != null) params.set("limit", String(page.limit));
    if (page.offset != null) params.set("offset", String(page.offset));
    return request(`${url("/api/admin/users")}?${params.toString()}`);
  },

  accountDetail(id: string): Promise<Schemas["AdminUserDetailOutputBody"]> {
    return request(url("/api/admin/users/{id}", { id }));
  },

  /**
   * Set one account's override for one feature (decision 524).
   *
   * THE THREE STATES ARE THE CLI VERB'S, one for one — `ingest user-feature
   * <email> <feature> <on|off|default>` — so the two ways of setting a grant
   * cannot rule differently about the same account. `default` removes the
   * override rather than storing `off`, which is the distinction decision 369
   * rests on.
   *
   * Answers the same `features` array the detail read sends. The screen refetches
   * the detail anyway rather than holding this (decision 513's posture: the
   * server decides what the row is), so the return is here for the type rather
   * than for a caller to depend on.
   */
  setAccountFeature(
    id: string,
    feature: string,
    override: "on" | "off" | "default",
  ): Promise<Schemas["AdminUserFeaturesOutputBody"]> {
    return request(url("/api/admin/users/{id}/features/{feature}", { id, feature }), {
      method: "PUT",
      body: JSON.stringify({ override }),
    });
  },

  deleteAccount(id: string, email: string): Promise<unknown> {
    return request(url("/api/admin/users/{id}", { id }), {
      method: "DELETE",
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Corpus counts and nightly job health (decision 483).
   *
   * One request for the whole page rather than one per section: it is read
   * whole, and eight requests to render one screen is eight chances for half of
   * it to arrive.
   */
  metrics(): Promise<Schemas["AdminMetricsOutputBody"]> {
    return request(url("/api/admin/metrics"));
  },

  /**
   * Every account holding a push subscription, with each device's User-Agent
   * and enrolment instant — never the endpoint or keys (decision 489).
   */
  pushDevices(): Promise<Schemas["AdminPushDevicesOutputBody"]> {
    return request(url("/api/admin/push-devices"));
  },

  /**
   * The active event split policies (decision 554), read-only: they are
   * changed with `ingest split-policy-set`.
   */
  splitPolicy(): Promise<Schemas["AdminSplitPolicyOutputBody"]> {
    return request(url("/api/admin/split-policy"));
  },

  /**
   * The ingestion health check's findings, the week's fetch failures, and the
   * newest sweep and check runs (roadmap stage 5). One read for the page, as
   * for metrics.
   */
  health(): Promise<Schemas["AdminHealthOutputBody"]> {
    return request(url("/api/admin/health"));
  },
};

/**
 * Arrays in this contract arrive as `T[] | null`.
 *
 * huma marks a slice field non-optional and nullable, so the generated type is a
 * union even where the Go always sends `[]`. Normalising at the boundary is what
 * keeps `?? []` out of every `.map()` in the components — and it matters for
 * Active specifically, because an empty array is MEANINGFUL there: it is how the
 * client knows a group has no visible fencers and must be hidden.
 */
export function list<T>(v: T[] | null | undefined): T[] {
  return v ?? [];
}
